/**
 * A conversation's files: every attachment in what has loaded, newest first,
 * with who sent it and when. Each opens its Cloud link in a new tab.
 *
 * Only what has loaded. Older history is fetched as the transcript scrolls, and
 * this list says nothing about files further back rather than pretend to know.
 */

import { ds } from '../../ds';
import { useT } from '../../i18n';
import { formatDay } from '../../i18n/format';
import { useStore } from '../../lib/relay/store';
import { attachmentsIn } from '../../lib/relay/cloud-picker';
import type { Conversation } from '../../lib/relay/types';
import './chat.css';

export function FilesDialog({
  conversation,
  onClose
}: {
  conversation: Conversation;
  onClose: () => void;
}) {
  const { Dialog, Icon } = ds();
  const t = useT();
  const messages = useStore((s) => s.messages(conversation.id));
  const people = useStore((s) => s.people(conversation.members));
  const files = attachmentsIn(messages);

  return (
    <Dialog open position="fixed" title={t('conversation.files')} onClose={onClose}>
      {files.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>{t('conversation.files.none')}</p>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          {files.map(({ file, message }, i) => {
            const sender = people.find((p) => p.id === message.senderId)?.name ?? '';
            const line = [sender, formatDay(message.at)].filter(Boolean).join(' · ');
            const body = (
              <>
                <Icon name="File" size={16} />
                <span style={{ minWidth: 0 }}>
                  <span className="rl-truncate" style={{ display: 'block', fontWeight: 500 }}>
                    {file.name}
                  </span>
                  <span className="rl-truncate" style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)' }}>
                    {line}
                  </span>
                </span>
              </>
            );
            const row = {
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-8)',
              padding: 'var(--space-8) var(--space-12)',
              borderRadius: 'var(--radius-md, 10px)',
              background: 'var(--app-panel-sunken)',
              textDecoration: 'none',
              color: 'inherit'
            } as const;
            return file.cloudUrl ? (
              <a
                key={`${message.id}-${i}`}
                href={file.cloudUrl}
                target="_blank"
                rel="noreferrer"
                title={t('conversation.attachment.open')}
                style={row}
              >
                {body}
              </a>
            ) : (
              <div key={`${message.id}-${i}`} style={row}>
                {body}
              </div>
            );
          })}
        </div>
      )}
    </Dialog>
  );
}
